import {
    GridValidRowModel,
    GridRowSelectionModel,
    useGridApiRef,
    GridPreferencePanelsValue,
    GridColumnVisibilityModel,
    GridColumnOrderChangeParams,
    GridColDef,
    GridRowParams,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridSortModel,
    GridColumnResizeParams,
} from '@mui/x-data-grid-pro';
import React, { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import {
    IDocDefaultFields,
    IFieldConfig,
} from '../../../redux//project/documents/sliceProjectDocuments';
import { IAppReduxStore } from '../../../redux/store';
import {
    dateSort,
    getMultilineFields,
    moveInArray,
    numberSort,
    stringSort,
} from '../../../util/DataUtils';
import DocTableAdditionalDocs from './DocTableAdditionalDocs';
import {
    createActionColDef,
    getCategoryRows,
    getColumns,
    getSelectedDocIds,
    processDocuments,
} from './DocTableUtils';
import StyledDataGrid from './Styled/StyledDataGridPro';
import { updateSelectedDocuments } from '../../../redux/project/documents/sliceProjectSelectedDocuments';
import { useTranslation } from 'react-i18next';
import DocTableMenu from './DocTableMenu';
import { ErrorBoundary } from 'react-error-boundary';
import DocTableError from './DocTableError';
import {
    areFieldsEqual,
    getCurrentFieldsOrFieldsSchema,
    setDataToSave,
} from '../../../redux/project/sliceProjectViewData';
import {
    setHasFilteredItems,
    setShowColumnsMenu,
    setShowFilter,
} from '../../../redux/user/sliceUserSettings';
import { isDDMMYYY } from '../../../util/StringUtils';
import DocTableToolbar from './DocTableToolbar';

/** Holds the value of the selected document DocType on context menu. */
let selectedDocumentDocType: string;
/** Holds the value of the selected document UniversalID on context menu. */
let selectedDocumentUniversalId: string;

let currentDocument: IDocDefaultFields;

/** Offset in x direction for the context menu */
const offsetX = 2;
/** Offset in y direction for the context menu */
const offsetY = 4;
/**
 * Component that shows project database documents in an interactive DataGrid.
 */
export default () => {
    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    const { i18n, t } = useTranslation('translations', { keyPrefix: 'docTable' });

    /** Get relevant values from Redux store. */
    const documents = useSelector((store: IAppReduxStore) => store.projectDocuments.data);
    const docTypes = useSelector((store: IAppReduxStore) => store.projectDocTypes.data);
    const documentsLoading = useSelector((store: IAppReduxStore) => store.projectDocuments.loading);
    const { loadingRunDynamicAction } = useSelector(
        (store: IAppReduxStore) => store.projectDocumentsDynamicSelection,
    );
    const fields = useSelector(getCurrentFieldsOrFieldsSchema('fields'));
    const fieldSchema = useSelector(getCurrentFieldsOrFieldsSchema<string>('schema'));
    const defineCustomColumnsEquality = useSelector(areFieldsEqual);

    const viewStructure = useSelector(
        (store: IAppReduxStore) => store.projectViewData.structure.data,
    );

    const selectedDocuments = useSelector(
        (store: IAppReduxStore) => store.projectSelectedDocuments.storage,
    );
    /** Get relevant values from Redux store. */
    const allAdditionalDocuments = useSelector(
        (store: IAppReduxStore) => store.projectAdditionalDocs.documents.data,
    );
    const documentsToExpandNoteIds = useSelector(
        (store: IAppReduxStore) => store.projectAdditionalDocs.documentsToExpandNoteIds,
    );
    const { showFilter, showColumnsMenu } = useSelector(
        (store: IAppReduxStore) => store.userSettings,
    );

    const { replicaId, areaName, category } = useParams();

    const [columns, setColumns] = useState<GridColDef[]>([]);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
        {},
    );

    const [localCustomColumnsView, setLocalCustomColumnsView] = useState({
        fields: [],
        fieldSchema: [],
    });
    const [initialSorted, setInitialSorted] = useState<boolean>(false);

    /** Add component keyboard events. */
    useEffect(() => {
        window.addEventListener('keydown', handleKeydown);

        /** Remove the listener when the component is no longer needed. */
        return () => {
            window.removeEventListener('keydown', handleKeydown);
        };
    }, []);

    /**
     * Creates the Master Detail area for a given DataGrid row.
     * @param row - Target DataGrid row.
     */
    const getAdditionalDocuments = useCallback(
        ({ row }: GridRowParams) => {
            return <DocTableAdditionalDocs row={row} />;
        },
        [columns],
    );

    /**
     * Creates the row objects for rendering in the DataGrid.
     * @returns Target DataGrid rows.
     */
    const rows: GridValidRowModel[] = useMemo((): GridValidRowModel[] => {
        let categories = getCategoryRows(viewStructure, category).filter(Boolean);

        if (categories.length) {
            const duedays = categories
                .filter(s => !isNaN(s.CategoryName))
                .sort((a, b) => numberSort(a.CategoryName, b.CategoryName));
            const dates = categories
                .filter(s => isDDMMYYY(s.CategoryName))
                .sort((a, b) => dateSort(a.CategoryName, b.CategoryName))
                .reverse();
            const strings = categories
                .filter(s => isNaN(s.CategoryName) && !isDDMMYYY(s.CategoryName))
                .sort((a, b) => stringSort(a.CategoryName, b.CategoryName));

            categories = [...duedays, ...dates, ...strings];
        }

        const docs = processDocuments(documents, fields);

        return [...categories, ...docs];
    }, [viewStructure, category, documents, fields, fieldSchema]);

    const initialSortModel = useMemo((): GridSortModel => {
        const initialSortField = fields?.find(x => x.sorted)?.field;
        return initialSortField
            ? [
                  {
                      field: initialSortField,
                      sort: 'asc',
                  },
              ]
            : undefined;
    }, [fields]);

    /**
     * Overrides CTRL+A sequence and Escape key to manipulate data grid row selection.
     * @param e - Keyboard event.
     */
    const handleKeydown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setSelectionModel([]);

                return;
            }

            if (/a/i.test(e.key) && e.ctrlKey && documents?.length) {
                e.preventDefault();
                e.stopPropagation();

                setSelectionModel(documents.map(x => x.NoteId));
            }
        },
        [documents],
    );

    /** Create column definitions. */
    useEffect(() => {
        setColumns(() => [
            {
                ...GRID_CHECKBOX_SELECTION_COL_DEF,
                hideable: false,
            },
            createActionColDef(localCustomColumnsView.fieldSchema, docTypes, t),
            ...getColumns(
                localCustomColumnsView.fieldSchema,
                localCustomColumnsView.fields,
                rows,
                t,
            ).filter(Boolean),
        ]);
    }, [
        localCustomColumnsView.fields,
        localCustomColumnsView.fieldSchema,
        docTypes,
        rows,
        i18n.language,
    ]);

    /**
     * Updates the collection of selected documents.
     * @param rowSelectionModel -> Array of selected document ids from the DataGrid.
     */
    const setSelectionModel = useCallback(
        (rowSelectionModel: GridRowSelectionModel) => {
            const documentIds = getSelectedDocIds(rows, selectedDocuments, rowSelectionModel);
            dispatch(
                updateSelectedDocuments({
                    replicaId,
                    areaName,
                    documentIds,
                }),
            );
        },
        [rows, selectedDocuments],
    );

    const gridApiRef = useGridApiRef();

    /** Changes the state from the coordinations of the mouse click to false. */
    const [contextMenuPosition, setContextMenuPosition] = React.useState<{
        mouseX: number;
        mouseY: number;
    } | null>(null);

    /** Handles the context menu for each individual document row. */
    const handleContextMenu = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.preventDefault();

        setContextMenuPosition({
            mouseX: event.clientX - offsetX,
            mouseY: event.clientY - offsetY,
        });

        const rowId = event.currentTarget.dataset.id;
        const row = rows.find(el => el.NoteId === rowId);

        selectedDocumentDocType = row.DocType || row.docType;
        selectedDocumentUniversalId = row.UniversalID;
        currentDocument = row as IDocDefaultFields;
    };
    useEffect(() => {
        setLocalCustomColumnsView(prev => ({
            ...prev,
            fields,
            fieldSchema,
        }));
    }, [fields, fieldSchema]);

    useEffect(() => {
        const columnVisibilityModel = fields.reduce((obj, field) => {
            obj[field.field] = field.visible === undefined ? true : Boolean(field.visible);
            return obj;
        }, {});

        setColumnVisibilityModel(columnVisibilityModel);

        gridApiRef.current.setColumnVisibilityModel(columnVisibilityModel);
    }, [fields, fieldSchema, gridApiRef]);

    useEffect(() => {
        if (documentsToExpandNoteIds.length) {
            const rowIds = rows
                .filter(x => documentsToExpandNoteIds.includes(x.NoteId))
                .map(x => x.NoteId);
            gridApiRef.current.setExpandedDetailPanels(rowIds);
        }
    }, [rows, allAdditionalDocuments, documentsToExpandNoteIds]);

    useEffect(() => {
        showFilter
            ? gridApiRef.current?.showPreferences(GridPreferencePanelsValue.filters)
            : gridApiRef.current?.hidePreferences();
    }, [showFilter]);

    useEffect(() => {
        showColumnsMenu
            ? gridApiRef.current?.showPreferences(GridPreferencePanelsValue.columns)
            : gridApiRef.current?.hidePreferences();
    }, [showColumnsMenu]);

    useEffect(() => {
        dispatch(setShowFilter(false));
        dispatch(setShowColumnsMenu(false));
        dispatch(setDataToSave(null));
        dispatch(setHasFilteredItems(false));
    }, [replicaId, areaName, category]);

    useEffect(() => {
        if (!localCustomColumnsView.fields.length || !localCustomColumnsView.fieldSchema.length) {
            return;
        }
        dispatch(
            setDataToSave(
                defineCustomColumnsEquality(localCustomColumnsView.fields)
                    ? null
                    : {
                          fields: JSON.stringify(localCustomColumnsView.fields),
                          fieldSchema: JSON.stringify(localCustomColumnsView.fieldSchema),
                      },
            ),
        );
    }, [localCustomColumnsView.fields]);

    useEffect(() => {
        // 7750: collapse additionalDocs when switching viewStructure
        gridApiRef.current.setExpandedDetailPanels([]);
    }, [gridApiRef, rows]);

    const fieldsWithMultilineType = useMemo(() => getMultilineFields(fields), [fields]);

    const handleColumnModelChange = (newModel: GridColumnVisibilityModel) => {
        if (fields.length === 0 || fieldSchema.length === 0 || Object.keys(newModel).length === 0)
            return;
        const newCustomFields = fields.map(field =>
            Object.keys(newModel).includes(field.field)
                ? { ...field, visible: newModel[field.field] }
                : field,
        );
        setLocalCustomColumnsView(prev => ({
            ...prev,
            fields: newCustomFields,
        }));
    };

    const onColumnSizeChange = ({ colDef, width }: GridColumnResizeParams) => {
        const newCustomFields = localCustomColumnsView.fields.map(field =>
            field.field === colDef.field ? { ...field, width } : field,
        );
        setLocalCustomColumnsView(prev => ({
            ...prev,
            fields: newCustomFields,
        }));
    };

    const handleColumnModel = ({ oldIndex, targetIndex, column }: GridColumnOrderChangeParams) => {
        // There is additional columns (action, checkBox), and oldIndex, targetIndex calculation based on all columns.
        const oldIndexForFields = localCustomColumnsView.fields.findIndex(
            field => field.field === column.field,
        );
        const indexDiff = oldIndex - oldIndexForFields;
        const newCustomFields = moveInArray<IFieldConfig[]>(
            [...localCustomColumnsView.fields],
            oldIndexForFields,
            targetIndex - indexDiff,
        );
        const newCustomFieldsSchema = moveInArray<string[]>(
            [...localCustomColumnsView.fieldSchema],
            oldIndexForFields,
            targetIndex - indexDiff,
        );
        setLocalCustomColumnsView(prev => ({
            ...prev,
            fields: newCustomFields,
            fieldSchema: newCustomFieldsSchema,
        }));
    };

    useEffect(() => {
        if (!initialSorted && initialSortModel && documents?.length > 0) {
            gridApiRef.current?.setSortModel(initialSortModel);
            setInitialSorted(true);
        }
    }, [initialSorted, initialSortModel, documents]);

    return (
        <>
            <ErrorBoundary fallback={<DocTableError />}>
                <StyledDataGrid
                    apiRef={gridApiRef}
                    /** Auto-expand size to fit container element. */
                    sx={{
                        '& .MuiDataGrid-main': { WebkitBorderTopLeftRadius: 4 },
                        flexGrow: 4,
                        cursor: 'default',
                    }}
                    /** Column configurations. */
                    columns={columns}
                    /** Update the selection model when the user selects or de-selects a row. */
                    onRowSelectionModelChange={setSelectionModel}
                    /** Populate the row's Master Detail section (if document has additional documents). */
                    getDetailPanelContent={columns.length ? getAdditionalDocuments : null}
                    /** Make DataGrid selection model controlled. */
                    rowSelectionModel={rows
                        .filter(
                            x => (selectedDocuments?.documentIds ?? []).indexOf(x.UniversalID) >= 0,
                        )
                        .map(x => x.NoteId)}
                    /** Set DataGrid loading state. */
                    loading={documentsLoading || loadingRunDynamicAction}
                    /** Define row data. */
                    rows={rows}
                    slots={{
                        toolbar: DocTableToolbar,
                    }}
                    /** Context menu. */
                    slotProps={{
                        row: { onContextMenu: handleContextMenu },
                    }}
                    initialState={{
                        columns: {
                            columnVisibilityModel,
                        },
                    }}
                    sortingOrder={['asc', 'desc']}
                    onPreferencePanelClose={({ openedPanelValue }) => {
                        openedPanelValue === 'filters' && dispatch(setShowFilter(false));
                        openedPanelValue === 'columns' && dispatch(setShowColumnsMenu(false));
                    }}
                    onColumnVisibilityModelChange={newModel => {
                        handleColumnModelChange(newModel);
                    }}
                    onColumnResize={newModel => onColumnSizeChange(newModel)}
                    onColumnOrderChange={newModel => handleColumnModel(newModel)}
                    onFilterModelChange={newFilterModel => {
                        dispatch(
                            setHasFilteredItems(newFilterModel.items.some(item => item.value)),
                        );
                    }}
                    fieldsWithMultilineType={fieldsWithMultilineType}
                />
            </ErrorBoundary>

            <DocTableMenu
                docType={selectedDocumentDocType}
                universalId={selectedDocumentUniversalId}
                contextMenuPos={contextMenuPosition}
                setContextMenu={setContextMenuPosition}
                currentDocument={currentDocument}
            />
        </>
    );
};
